import type { PumsemTree, PumsemTreeNode } from '../model/pumsem.type';
import type { PumsemNodeHit } from '../model/pumsemTree.type';

/**
 * 코드의 조상 코드들. `6-2-4` → `['6', '6-2']`.
 *
 * 코드는 채번 시점부터 **접두사 중첩**이다(BE `nextSortOrder` — 장 `3` 아래
 * 절이 `3-1`, 그 아래 항목이 `3-1-1`). 그래서 트리를 뒤지지 않고 문자열만으로
 * 조상을 안다 — 목차를 펼칠 때 트리가 아직 안 왔어도 계산이 된다.
 */
export function pumsemAncestorCodes(code: string): string[] {
  const parts = code.split('-');
  return parts
    .slice(0, -1)
    .map((_, index) => parts.slice(0, index + 1).join('-'));
}

/**
 * 부문 + 코드로 트리 마디를 찾는다.
 *
 * 코드가 **항목이 아니라 장·절**일 수 있다는 게 이 함수의 존재 이유다.
 * `/pumsem/machine/6-2` 같은 주소는 상세 API 로는 404 인데, 사용자에게는
 * "없는 코드" 가 아니라 그 아래 목차를 보여줘야 한다. 트리는 이미 화면이
 * 들고 있으므로(사이드바 목차) 추가 왕복 없이 판별된다.
 */
export function findPumsemNode(
  tree: PumsemTree | undefined,
  divisionSlug: string | undefined,
  code: string | undefined,
): PumsemNodeHit | null {
  if (!tree || !divisionSlug || !code) return null;

  const division = tree.divisions.find((item) => item.slug === divisionSlug);
  if (!division) return null;

  const walk = (
    nodes: PumsemTreeNode[],
    trail: PumsemTreeNode[],
  ): PumsemNodeHit | null => {
    for (const node of nodes) {
      if (node.code === code) return { division, node, ancestors: trail };
      const hit = walk(node.children, [...trail, node]);
      if (hit) return hit;
    }
    return null;
  };

  return walk(division.nodes, []);
}

/** 마디 아래의 항목 수(재귀). 목차에 "12개 항목" 을 적기 위한 것. */
export function countPumsemItems(node: PumsemTreeNode): number {
  if (node.isItem) return 1;
  return node.children.reduce((sum, child) => sum + countPumsemItems(child), 0);
}
